import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Sparkles, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }); }
  catch { return iso; }
};

export default function GanapathiChat() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/ganapathi/owner-chat');
      setMessages(r.data || []);
    } catch (e) {
      toast({ title: 'Failed to load', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const t = input.trim();
    if (!t) return;
    setSending(true);
    // optimistic
    setMessages(m => [...m, { id: 'tmp', role: 'owner', content: t, timestamp: new Date().toISOString() }]);
    setInput('');
    try {
      await api.post('/ganapathi/owner-chat', { text: t });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-emerald-700 p-2" data-testid="gp-chat-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold truncate">Ganapathi</h1>
            <p className="text-[11px] text-emerald-100">Private chat · Owner only</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-2" data-testid="gp-chat-messages">
        {loading && <p className="text-center text-gray-400 text-sm">Loading…</p>}
        {!loading && messages.length === 0 && (
          <div className="text-center py-12 px-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Sparkles className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-sm font-semibold text-gray-700">Namaste sir 🙏</p>
            <p className="text-xs text-gray-500 mt-2 max-w-xs mx-auto">
              This is your private line with me. Give me any rule to follow, ask about the team,
              or discuss anything you&apos;d like corrected. Every instruction you give will be honoured going forward.
            </p>
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.role === 'owner' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${
              m.role === 'owner' ? 'bg-[#DCF8C6] text-gray-800' : 'bg-white border border-gray-200 text-gray-800'
            }`} data-testid={`gp-chat-msg-${m.id}`}>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</p>
              {m.saved_as_directive_id && (
                <p className="text-[10px] text-emerald-700 mt-1">✓ Saved as active directive</p>
              )}
              {m.conflict && (
                <div className="mt-2 pt-2 border-t border-amber-200 flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] text-amber-700">
                    Conflict with existing directive — please clarify in your next message.
                  </p>
                </div>
              )}
              <p className="text-[10px] text-gray-400 mt-1 text-right">{fmtTime(m.timestamp)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="border-t bg-white px-3 py-2 sticky bottom-0">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder="Message Ganapathi…"
            className="flex-1 border border-gray-200 rounded-2xl px-3 py-2 text-sm resize-none max-h-24 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            data-testid="gp-chat-input"
          />
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-10 w-10 p-0 flex-shrink-0"
            onClick={send}
            disabled={sending || !input.trim()}
            data-testid="gp-chat-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
